import moment from 'moment';
import MealType from '../../models/MealType';
import { mealKey } from './normalize';

/**
 * A calendar day's meals and slot notes as the meal screens show them (docs/local-first-food-data.md,
 * phase 4): `{ date, slots, slotNotes }`, where `date` is 'Y-m-d', `slots` holds each slot's meals in
 * their order, keyed by MealType.normalize(type), and `slotNotes` each slot's note by the same key.
 * Plain objects built from the food store's entities; selectDayEvents() in selectors.js gives one per
 * day, and the functions here read them.
 *
 * A meal with no name is left out: the old day view saved its blank input rows as meals.
 */

const NO_MEALS = Object.freeze([]);

/** The slot a meal of `type` sits in: one of the four, or Other (0). */
export const slotOf = (type) => MealType.normalize(type);

const numeric = (value) => (value === null || value === undefined || value === '' ? null : Number(value));

/** A slot's order, as the server keeps it: by `order`, then by id. A meal made here has none yet, and goes by its uid. */
export const byPlace = (a, b) => ((Number(a.order) || 0) - (Number(b.order) || 0)) ||
	(numeric(a.id) === null ? 1 : 0) - (numeric(b.id) === null ? 1 : 0) ||
	((numeric(a.id) || 0) - (numeric(b.id) || 0)) ||
	String(a.uid || '').localeCompare(String(b.uid || ''));

/**
 * The day `date` ('Y-m-d', or a moment) with `meals`, the meals the store has on it, and
 * `slotNotes`: the slot note entities on it, or `{ [type]: notes }`.
 */
export const dayEventOf = (date, meals = [], slotNotes = []) => {
	const slots = {};
	meals.filter((meal) => meal && meal.name).forEach((meal) => {
		const slot = slotOf(meal.type);
		(slots[slot] = slots[slot] || []).push(meal);
	});
	Object.values(slots).forEach((list) => list.sort(byPlace));
	const notes = {};
	if(Array.isArray(slotNotes)) {
		slotNotes.filter((note) => note && note.notes).forEach((note) => {
			notes[slotOf(note.type)] = note.notes;
		});
	} else {
		Object.keys(slotNotes || {}).filter((type) => slotNotes[type]).forEach((type) => {
			notes[slotOf(type)] = slotNotes[type];
		});
	}
	return { date: moment.isMoment(date) ? date.format('YYYY-MM-DD') : date, slots, slotNotes: notes };
};

/** The meals in slot `type` of `dayEvent`, in their order. None for a day the store has nothing on. */
export const mealsInSlot = (dayEvent, type) => (dayEvent && dayEvent.slots[slotOf(type)]) || NO_MEALS;

/** The note on slot `type`, or '' for none. */
export const slotNoteOf = (dayEvent, type) => (dayEvent && dayEvent.slotNotes[slotOf(type)]) || '';

/**
 * Whether slot `type` of `dayEvent` holds a meal made from idea `mealIdeaId`, other than the meal
 * keyed `exceptKey` (mealKey()). A slot holds an idea at most once: two Taco Salads at lunch is a
 * mistake, not a plan.
 */
export const dayHasMealIdeaInSlot = (dayEvent, type, mealIdeaId, exceptKey = null) => {
	if(mealIdeaId === null || mealIdeaId === undefined) {
		return false;
	}
	return mealsInSlot(dayEvent, type).some((meal) => mealKey(meal) !== exceptKey &&
		meal.meal_idea_id !== null && meal.meal_idea_id !== undefined &&
		Number(meal.meal_idea_id) === Number(mealIdeaId));
};
